const REVIEW_FORM_API = 'https://api-cdn.yotpo.com';
const REVIEW_FORM_MAX_IMAGES = 10;
const REVIEW_FORM_EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

class ReviewFormModal extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.form = this.querySelector('[data-review-form]');
    this.success = this.querySelector('[data-review-success]');
    this.closeButton = this.querySelector('.review-form_close');
    this.submitButton = this.querySelector('.review-form_submit');
    this.questionsElement = this.querySelector('[data-review-questions]');
    this.thumbsElement = this.querySelector('[data-review-thumbs]');
    this.fileInput = this.querySelector('input[type="file"]');
    this.formError = this.querySelector('[data-form-error]');
    this.images = [];

    this.querySelector('.review-form_overlay').addEventListener('click', () => this.close());
    this.closeButton.addEventListener('click', () => this.close());
    this.querySelector('.review-form_done').addEventListener('click', () => this.close());
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.form.addEventListener('submit', (event) => this.handleSubmit(event));
    this.form.addEventListener('input', (event) => {
      const field = event.target.closest('[data-field]');
      if (field) this.hideError(field);
    });
    this.fileInput.addEventListener('change', () => this.addImages());

    const inner = this.querySelector('.review-form_inner');
    bindSwipeDownToClose(inner, this.querySelector('.review-form_body'), () => this.close());
    window.addEventListener('pageshow', (event) => event.persisted && this.close());
  }

  open(opener) {
    this.opener = opener;
    this.loadQuestions();
    this.form.hidden = false;
    this.success.hidden = true;
    this.removeAttribute('inert');
    document.body.classList.add('overflow-hidden');
    trapFocus(this, this.closeButton);
  }

  close() {
    if (this.hasAttribute('inert')) return;
    this.setAttribute('inert', '');
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(this.opener);
  }

  /* ---- Questions (configured in Yotpo) ---- */

  loadQuestions() {
    if (this.questionsRequest) return this.questionsRequest;

    const { appKey, productId } = this.dataset;
    this.questionsRequest = fetch(`${REVIEW_FORM_API}/v3/storefront/store/${appKey}/product/${productId}/productFilters`)
      .then((response) => response.json())
      .then((data) => {
        this.questions = (data.customQuestions || []).filter((question) => question.options?.length);
        this.questions.forEach((question) => this.questionsElement.appendChild(this.renderQuestion(question)));
      })
      .catch((error) => {
        this.questions = [];
        console.error(error);
      });
    return this.questionsRequest;
  }

  renderQuestion(question) {
    const multiple = question.fieldType === 'MultipleChoice';
    const fieldset = document.createElement('fieldset');
    fieldset.className = 'review-form_field';
    fieldset.dataset.field = `question-${question.questionId}`;

    const legend = document.createElement('legend');
    legend.className = 'review-form_label';
    legend.textContent = `${question.question}${question.mandatory ? ' *' : ''} `;
    const hint = document.createElement('span');
    hint.className = 'review-form_hint';
    hint.textContent = multiple ? 'Choose all that apply' : 'Choose 1';
    legend.appendChild(hint);

    const options = document.createElement('div');
    options.className = 'review-form_options';
    question.options.forEach((option, index) => {
      const id = `${this.id}-q${question.questionId}-${index}`;
      const input = document.createElement('input');
      input.type = multiple ? 'checkbox' : 'radio';
      input.className = 'visually-hidden';
      input.name = `question-${question.questionId}`;
      input.id = id;
      // Size and Rating questions store the option's 1-based position; the others store its text.
      input.value = ['Size', 'Rating'].includes(question.fieldType) ? String(index + 1) : option;

      const label = document.createElement('label');
      label.className = 'review-form_option';
      label.htmlFor = id;
      label.textContent = option.trim();
      options.append(input, label);
    });

    const error = document.createElement('small');
    error.className = 'input-error hidden';
    error.appendChild(document.createElement('span'));

    fieldset.append(legend, options, error);
    return fieldset;
  }

  /* ---- Photos ---- */

  addImages() {
    const files = [...this.fileInput.files].filter((file) => file.type.startsWith('image/'));
    this.images = [...this.images, ...files].slice(0, REVIEW_FORM_MAX_IMAGES);
    this.fileInput.value = '';
    this.renderThumbs();
  }

  renderThumbs() {
    this.thumbsElement.querySelectorAll('img').forEach((image) => URL.revokeObjectURL(image.src));
    this.thumbsElement.replaceChildren(
      ...this.images.map((file, index) => {
        const item = document.createElement('li');
        item.className = 'review-form_thumb';
        const image = document.createElement('img');
        image.src = URL.createObjectURL(file);
        image.alt = file.name;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'review-form_thumb-remove unstyled-button';
        remove.setAttribute('aria-label', `Remove ${file.name}`);
        remove.appendChild(this.closeButton.querySelector('svg').cloneNode(true));
        remove.addEventListener('click', () => {
          this.images.splice(index, 1);
          this.renderThumbs();
        });
        item.append(image, remove);
        return item;
      })
    );
    this.thumbsElement.hidden = !this.images.length;
  }

  /* ---- Validation ---- */

  showError(field, message) {
    field.classList.add('error');
    const error = field.querySelector('.input-error');
    error.querySelector('span').textContent = message;
    error.classList.remove('hidden');
  }

  hideError(field) {
    field.classList.remove('error');
    field.querySelector('.input-error')?.classList.add('hidden');
  }

  validate() {
    const data = new FormData(this.form);
    const errors = [];
    const check = (name, valid, message) => {
      const field = this.form.querySelector(`[data-field="${name}"]`);
      if (valid) {
        this.hideError(field);
      } else {
        this.showError(field, message);
        errors.push(field);
      }
    };

    check('rating', data.get('review_score'), 'Please choose a rating.');
    check('content', data.get('review_content').trim(), 'Please write a review.');
    check('title', data.get('review_title').trim(), 'Please add a headline.');
    check('name', data.get('display_name').trim(), 'Please enter your name.');
    check('email', REVIEW_FORM_EMAIL_PATTERN.test(data.get('email').trim()), 'Please enter a valid email address.');
    (this.questions || [])
      .filter((question) => question.mandatory)
      .forEach((question) => {
        check(`question-${question.questionId}`, data.get(`question-${question.questionId}`), 'Please choose an option.');
      });

    if (errors.length) errors[0].querySelector('input, textarea')?.focus();
    return !errors.length;
  }

  /* ---- Submit (the same request Yotpo's own form sends) ---- */

  async handleSubmit(event) {
    event.preventDefault();
    this.formError.classList.add('hidden');
    await this.loadQuestions();
    if (!this.validate()) return;

    this.submitButton.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${REVIEW_FORM_API}/v1/widget/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: this.buildParams(),
      });
      if (!response.ok) throw new Error(`Yotpo responded ${response.status}`);

      const result = await response.json().catch(() => ({}));
      if (result.code && result.code !== 200) throw new Error(result.message || `Yotpo code ${result.code}`);
      if (this.images.length && result.image_upload_token) {
        await this.uploadImages(result.image_upload_token).catch((error) => console.error(error));
      }
      this.showSuccess();
    } catch (error) {
      console.error(error);
      this.formError.querySelector('span').textContent = 'Something went wrong. Please try again.';
      this.formError.classList.remove('hidden');
    } finally {
      this.submitButton.removeAttribute('aria-busy');
    }
  }

  buildParams() {
    const data = new FormData(this.form);
    const { appKey, productId, productTitle, productDescription, productUrl, productImageUrl } = this.dataset;
    const params = new URLSearchParams({
      review_score: data.get('review_score'),
      review_title: data.get('review_title').trim(),
      review_content: data.get('review_content').trim(),
      display_name: data.get('display_name').trim(),
      email: data.get('email').trim(),
      appkey: appKey,
      review_source: 'widget_v2',
      sku: productId,
      product_title: productTitle,
      product_description: productDescription,
      product_url: productUrl,
      product_image_url: productImageUrl,
      uploaded_images: JSON.stringify(
        this.images.map((file) => ({ name: file.name, type: file.type, lastModified: file.lastModified }))
      ),
    });

    (this.questions || []).forEach((question) => {
      const values = data.getAll(`question-${question.questionId}`);
      if (!values.length) return;
      if (question.fieldType === 'MultipleChoice') {
        values.forEach((value) => params.append(`custom_fields[--${question.questionId}][]`, value));
      } else {
        params.append(`custom_fields[--${question.questionId}]`, values[0]);
      }
    });
    return params;
  }

  async uploadImages(token) {
    const policyParams = new URLSearchParams({ policy_name: 'ReviewImages', app_key: this.dataset.appKey });
    const policy = await fetch('https://api.yotpo.com/s3_signature', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: policyParams,
    }).then((response) => response.json());
    const bucket = `https://${policy.bucket}.s3.amazonaws.com`;

    await Promise.allSettled(
      this.images.map(async (file) => {
        const key = `${policy.path}${token}/${file.lastModified}_${file.name}`;
        const upload = new FormData();
        upload.append('key', key);
        upload.append('acl', 'public-read');
        upload.append('Content-Type', file.type);
        upload.append('policy', policy.encoded_policy);
        upload.append('x-amz-credential', policy.credential);
        upload.append('x-amz-algorithm', 'AWS4-HMAC-SHA256');
        upload.append('x-amz-date', policy.date);
        upload.append('x-amz-meta-uuid', '14365123651274');
        upload.append('x-amz-signature', policy.signature);
        upload.append('file', file);
        await fetch(bucket, { method: 'POST', body: upload });

        await fetch(`${REVIEW_FORM_API}/images/process`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image_upload_token: token, image_urls: [`${bucket}/${key}`] }),
        });
      })
    );
  }

  showSuccess() {
    this.form.reset();
    this.images = [];
    this.renderThumbs();
    this.form.hidden = true;
    this.success.hidden = false;
    trapFocus(this, this.querySelector('.review-form_done'));
  }
}

customElements.define('review-form-modal', ReviewFormModal);
